import { createApi } from '@reduxjs/toolkit/query/react';
import baseQueryWithReauth from './baseQuery';

export interface Company {
  _id: string;
  name: string;
  code: string;
  address: string;
  contact_number: string;
  created_at: string;
  updated_at: string;
}

export interface CreateCompanyDto {
  name: string;
  code: string;
  address: string;
  contact_number: string;
}

export interface UpdateCompanyDto {
  name?: string;
  code?: string;
  address?: string;
  contact_number?: string;
}

export const companiesApi = createApi({
  reducerPath: 'companiesApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Companies'],
  endpoints: (builder) => ({
    getCompanies: builder.query<Company[], void>({
      query: () => '/companies',
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'Companies' as const, id: _id })),
              { type: 'Companies', id: 'LIST' },
            ]
          : [{ type: 'Companies', id: 'LIST' }],
    }),
    getCompany: builder.query<Company, string>({
      query: (id) => `/companies/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Companies', id }],
    }),
    createCompany: builder.mutation<Company, CreateCompanyDto>({
      query: (data) => ({
        url: '/companies',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Companies', id: 'LIST' }],
    }),
    updateCompany: builder.mutation<Company, { id: string; data: UpdateCompanyDto }>({
      query: ({ id, data }) => ({
        url: `/companies/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Companies', id },
        { type: 'Companies', id: 'LIST' },
      ],
    }),
    deleteCompany: builder.mutation<void, string>({
      query: (id) => ({
        url: `/companies/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Companies', id },
        { type: 'Companies', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetCompaniesQuery,
  useGetCompanyQuery,
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
  useDeleteCompanyMutation,
} = companiesApi; 